import { useFormikContext } from 'formik';
import { Checkbox, CheckboxProps, Stack } from '@mui/material';
import Field from './Field';
import type { FieldProps } from './Field';
import React from 'react';
import omit from 'lodash/omit';
import {
  TestIdPrefix,
  getPrefixedTestId,
} from '@watershed/shared-universal/utils/testUtils';

export interface CheckboxFieldProps
  extends Omit<
      FieldProps,
      | 'maxLength'
      | 'disableNullOption'
      | 'nullValueLabel'
      | 'fsUnmask'
      | 'validationPlacement'
      | 'inline'
    >,
    Omit<CheckboxProps, 'id' | 'value' | 'size'> {
  testId?: string;
}

export function CheckboxField<T extends Record<string, number | ''>>({
  ...props
}: CheckboxFieldProps) {
  const form = useFormikContext<T>();
  const isChecked = !!form.values[props.inputId];

  function handleValueChange(event: React.ChangeEvent<HTMLInputElement>) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    form.setFieldValue(props.inputId, event.target.checked);
  }

  return (
    <CheckboxFieldNonFormik
      checked={isChecked}
      onChange={handleValueChange}
      {...props}
    />
  );
}

export function CheckboxFieldNonFormik({
  sx,
  testId,
  ...props
}: CheckboxFieldProps) {
  return (
    <Stack
      direction="row"
      sx={sx}
      data-testid={getPrefixedTestId(TestIdPrefix.CheckboxField, props.inputId)}
    >
      <Checkbox
        {...omit(
          props,
          'inputId',
          'label',
          'sublabel',
          'testId',
          'validationMessage',
          'validationState'
        )}
        sx={{ marginBlockStart: 0.5 }}
        id={props.inputId}
        checked={props.checked ?? false}
        data-testid={
          testId ? getPrefixedTestId(TestIdPrefix.Checkbox, testId) : undefined
        }
      />
      <Field {...props} />
    </Stack>
  );
}
